// ============================================================
// Domain — Exposure packages and their inventory availability
// ============================================================

import moment from 'moment';
import { AssetGroup } from '../../../services/campaignManager/types';

export interface PackageAsset {
  id: string;
  name: string;
  group: AssetGroup;
}

export interface ExposurePackage {
  /** Local catalogue identifier. Only the static seed catalogue is keyed this way. */
  id: string;
  /** Server-side identifier. Present on everything the init-info API returns, and
   *  it — not `id` — is what gets stored on the opportunity and POSTed back as
   *  `packageTier`. Always resolve through `resolveExposurePackage`. */
  key?: string;
  country?: string;
  name: string;
  tier: string;
  minCommissionRate: number;
  color: string;
  summary?: string;
  assets: PackageAsset[];
  typeOptions?: string[];
}

/**
 * Resolve the ExposurePackage a stored ref points at.
 *
 * A ref is whatever the picker persisted onto the opportunity: the server `key`
 * when the API supplied one, otherwise the local catalogue `id`. Matching on
 * only one of the two silently yields `undefined` for the other data source, so
 * every read path must go through here rather than a bare `find` on `id`.
 *
 * `country` disambiguates keys that repeat across markets.
 */
export const resolveExposurePackage = (
  packages: ExposurePackage[],
  ref: string | null | undefined,
  country?: string
): ExposurePackage | undefined => {
  if (!ref) return undefined;
  return packages.find((p) =>
    p.key ? p.key === ref && (!country || !p.country || p.country === country) : p.id === ref
  );
};

/**
 * Return the availability state for a given package on a given day.
 * `null` package → no signal (no package selected yet).
 *
 * 2-state per design 2026-05-30: only `available` ↔ `sold-out`.
 * Sold-out is a visual hint; the user can still pick it. See `AvailabilityDatePicker`.
 *
 * The blocked-date map is injected rather than read from a module-level constant
 * so this stays usable with live inventory as well as the static seed data.
 */
export const availabilityFor = (
  blockedDatesByPackage: Record<string, string[]>,
  packageId: string | null | undefined,
  date: Date | moment.Moment
): 'healthy' | 'sold-out' | null => {
  if (!packageId) return null;
  const list = blockedDatesByPackage[packageId];
  if (!list) return 'healthy';
  const ymd = moment.isMoment(date) ? date.format('YYYY-MM-DD') : moment(date).format('YYYY-MM-DD');
  return list.includes(ymd) ? 'sold-out' : 'healthy';
};
